//Produtos.js

import React, { useEffect, useState, useRef } from 'react';
import api from '../../services/api';
import toast, { Toaster } from 'react-hot-toast';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {  
    Plus, ChevronDown, ListChecks, Coffee,  
    Image as ImageIcon, X, Trash2, AlertTriangle, GripVertical, Layers,
    UploadCloud, FileText, Table, Webhook, HelpCircle, Copy, Check, ChevronLeft, ChevronRight, Folder, CheckSquare, Square
} from 'lucide-react';
import '../../styles/SubComponentsAdmin.css';

const Produtos = ({ estabelecimentoId }) => {
    const fileInputRef = useRef(null);
    const importFileInputRef = useRef(null);

    const [produtos, setProdutos] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [gruposDisponiveis, setGruposDisponiveis] = useState([]); 
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [showPlanLimitModal, setShowPlanLimitModal] = useState(false);

    const [editandoId, setEditandoId] = useState(null);
    const [itemParaExcluir, setItemParaExcluir] = useState(null);
     
    const [imagePreview, setImagePreview] = useState(null);
    const [uploadFile, setUploadFile] = useState(null);
    const [importTipo, setImportTipo] = useState('csv');
    const [importandoLoading, setImportandoLoading] = useState(false);

    // Estados para Seleção Múltipla e Exclusão em Massa
    const [produtosSelecionados, setProdutosSelecionados] = useState([]);

    // Estados para Paginação por Categoria (8 produtos por página)
    const [paginaPorCategoria, setPaginaPorCategoria] = useState({});
    const itensPorPagina = 8;

    // Estados para controle do Webhook e Slug
    const [webhookAtivo, setWebhookAtivo] = useState(true);
    const [copiedCurl, setCopiedCurl] = useState(false);
    const [estabelecimentoSlug, setEstabelecimentoSlug] = useState('');

    const initialState = {
        nome: '',
        descricao: '',
        preco: '',
        estoque: 0,
        categoria_id: '',
        imagem_url: '',
        ativo: 1,
        destaque: 0,
        exibir_qrcode: 1, 
        adicionais_ids: [] 
    };

    const [novoProduto, setNovoProduto] = useState(initialState);

    const isBebida = () => {
        const cat = categorias.find(c => String(c.id) === String(novoProduto.categoria_id));
        const nome = cat ? cat.nome.toLowerCase() : '';
        return nome.includes('bebida') || nome.includes('refrigerante') || nome.includes('suco') || nome.includes('cerveja');
    };

    const carregarDados = async () => {
        if (!estabelecimentoId) return;
        try {
            const [resProdutos, resCategorias, resAdicionais, resEstab] = await Promise.all([
                api.get(`/cardapio/produtos/${estabelecimentoId}`),
                api.get(`/cardapio/categorias/${estabelecimentoId}`),
                api.get(`/cardapio/adicionais/${estabelecimentoId}`),
                api.get(`/cardapio/estabelecimentos/${estabelecimentoId}`).catch(() => ({ data: {} }))
            ]);

            setProdutos(resProdutos.data.sort((a, b) => a.ordem - b.ordem));
            setCategorias(resCategorias.data);
            
            if (resEstab.data) {
                if (resEstab.data.webhook_ativo !== undefined) {
                    setWebhookAtivo(resEstab.data.webhook_ativo === 1);
                }
                if (resEstab.data.slug) {
                    setEstabelecimentoSlug(resEstab.data.slug);
                }
            }
             
            const gruposTratados = resAdicionais.data.map(g => ({
                ...g,
                itens: typeof g.itens_json === 'string' 
                    ? JSON.parse(g.itens_json) 
                    : (g.itens_json || [])
            }));
             
            setGruposDisponiveis(gruposTratados);

            if (resCategorias.data.length > 0 && !editandoId) {
                setNovoProduto(prev => ({ 
                    ...prev, 
                    categoria_id: resCategorias.data[0].id 
                }));
            }

        } catch (e) {
            toast.error("Erro ao carregar dados.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { 
        carregarDados(); 
    }, [estabelecimentoId]);

    // --- FUNÇÕES DE SELEÇÃO MÚLTIPLA ---
    const toggleSelecionarProduto = (id) => {
        setProdutosSelecionados(prev => 
            prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
        );
    };

    const toggleSelecionarTodosDaCategoria = (produtosDaCat) => {
        const idsDaCat = produtosDaCat.map(p => p.id);
        const todosSelecionados = idsDaCat.every(id => produtosSelecionados.includes(id));
        
        if (todosSelecionados) {
            setProdutosSelecionados(prev => prev.filter(id => !idsDaCat.includes(id)));
        } else {
            setProdutosSelecionados(prev => {
                const novoSet = new Set([...prev, ...idsDaCat]);
                return Array.from(novoSet);
            });
        }
    };

    const handleExcluirSelecionados = async () => {
        if (produtosSelecionados.length === 0) return;
        if (!window.confirm(`Tem certeza que deseja excluir os ${produtosSelecionados.length} produtos selecionados?`)) return;

        try {
            await Promise.all(produtosSelecionados.map(id => api.delete(`/cardapio/produtos/${id}`)));
            toast.success(`${produtosSelecionados.length} produtos excluídos com sucesso!`);
            setProdutosSelecionados([]);
            carregarDados();
        } catch (e) {
            toast.error("Erro ao excluir alguns produtos selecionados.");
            carregarDados();
        }
    };

    const handleToggleWebhook = async () => {
        const novoStatus = !webhookAtivo;
        setWebhookAtivo(novoStatus);
        try {
            await api.put(`/cardapio/estabelecimentos/${estabelecimentoId}`, {
                webhook_ativo: novoStatus ? 1 : 0
            });
            toast.success(novoStatus ? "Webhook ativado com sucesso!" : "Webhook desativado.");
        } catch (e) {
            toast.success(novoStatus ? "Webhook ativado!" : "Webhook desativado.");
        }
    };

    const identificadorUrl = estabelecimentoSlug || estabelecimentoId;
    const webhookUrl = `https://app.universomidiadigital.com.br/api/cardapio/webhook/produtos/${identificadorUrl}`;

    const curlExemplo = `curl -X POST '${webhookUrl}' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "nome": "Hambúrguer Artesanal",
    "descricao": "Pão, carne 160g e queijo",
    "preco": 32.90,
    "estoque": 15,
    "categoria": "Lanches"
  }'`;

    const copiarCurl = () => {
        navigator.clipboard.writeText(curlExemplo);
        setCopiedCurl(true);
        toast.success("Comando cURL copiado para a área de transferência!");
        setTimeout(() => setCopiedCurl(false), 3000);
    };

    const onDragEndAdicionais = (result) => {
        if (!result.destination) return;
    
        const items = Array.from(novoProduto.adicionais_ids);
    
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
    
        setNovoProduto(prev => ({ 
            ...prev, 
            adicionais_ids: items 
        }));
    };

    const toggleGrupo = (grupoId) => {
        setNovoProduto(prev => {
            const ids = [...prev.adicionais_ids];
            const index = ids.findIndex(id => Number(id) === Number(grupoId));
            if (index > -1) ids.splice(index, 1);
            else ids.push(grupoId);
            return { ...prev, adicionais_ids: ids };
        });
    };

    const handleSalvarProduto = async (e) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append('estabelecimento_id', estabelecimentoId);
        formData.append('nome', novoProduto.nome);
        formData.append('descricao', novoProduto.descricao || '');
        formData.append('preco', parseFloat(String(novoProduto.preco).replace(',', '.')));
        formData.append('estoque', novoProduto.estoque);
        formData.append('categoria_id', novoProduto.categoria_id);
        formData.append('ativo', novoProduto.ativo);
        formData.append('destaque', novoProduto.destaque);
        formData.append('exibir_qrcode', novoProduto.exibir_qrcode !== undefined ? novoProduto.exibir_qrcode : 1);
         
        const idsFinais = isBebida() ? [] : novoProduto.adicionais_ids;
        formData.append('adicionais', JSON.stringify(idsFinais));
         
        if (uploadFile) formData.append('imagem', uploadFile);
        else formData.append('imagem_url', novoProduto.imagem_url || '');

        try {
            if (editandoId) {
                await api.put(`/cardapio/produtos/${editandoId}`, formData);
                toast.success("Sucesso!");
                fecharModal();
                carregarDados();
            } else {
                await api.post(`/cardapio/produtos`, formData);
                toast.success("Sucesso!");
                fecharModal();
                carregarDados();
            }
        } catch (err) { 
            const errorMsg = err.response?.data?.error || "Erro ao salvar.";
            // Verifica se o erro retornado do backend corresponde ao limite do plano atingido
            if (errorMsg.includes("Limite do plano atingido") || err.response?.status === 403) {
                setShowPlanLimitModal(true);
            } else {
                toast.error(errorMsg); 
            }
        }
    };

    const handleExcluirProduto = async () => {
        try {
            await api.delete(`/cardapio/produtos/${itemParaExcluir.id}`);
            toast.success("Excluído!");
            setShowDeleteModal(false);
            carregarDados();
        } catch (e) { toast.error("Erro ao excluir."); }
    };

    const handleImportarArquivo = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('estabelecimento_id', estabelecimentoId);
        formData.append('tipo', importTipo);
        formData.append('arquivo', file);

        setImportandoLoading(true);
        try {
            await api.post(`/cardapio/produtos/importar`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success("Produtos importados e carregados automaticamente com sucesso!");
            setShowImportModal(false);
            carregarDados();
        } catch (err) {
            const errorMsg = err.response?.data?.error || "Erro ao processar arquivo de importação.";
            if (errorMsg.includes("Limite do plano atingido") || err.response?.status === 403) {
                setShowPlanLimitModal(true);
            } else {
                toast.error(errorMsg);
            }
        } finally {
            setImportandoLoading(false);
            if (importFileInputRef.current) importFileInputRef.current.value = '';
        }
    };

    const fecharModal = () => {
        setShowModal(false);
        setEditandoId(null);
        setNovoProduto(initialState);
        setImagePreview(null);
        setUploadFile(null);
    };

    const mudarPaginaCategoria = (catId, novaPagina) => {
        setPaginaPorCategoria(prev => ({ ...prev, [catId]: novaPagina }));
    };

    if (loading) return <div className="loader-container"><div className="spinner"></div></div>;

    return (
        <div className="tab-content animate-fade-in" style={{ paddingBottom: '80px' }}>
            <Toaster position="top-right" />
             
            <div className="top-info" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', flexWrap: 'wrap', gap: '15px' }}>
                <div>
                    <h2 className="content-title" style={{margin: 0}}>Gestão de Produtos por Categoria</h2>
                    <p style={{color: '#64748b', fontSize: '14px'}}>Organize, selecione em massa e gerencie seus produtos divididos por categorias.</p>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button 
                        onClick={() => setShowImportModal(true)} 
                        className="btn-secondary" 
                        style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#f8fafc', border: '1px solid #cbd5e1', cursor: 'pointer', padding: '10px 16px', borderRadius: '8px', fontWeight: '600', fontSize: '13px' }}
                    >
                        <UploadCloud size={16} /> Importar Produtos
                    </button>
                    <button onClick={() => setShowModal(true)} className="btn-confirm">+ Novo Produto</button>
                </div>
            </div>

            {/* SEPARAÇÃO DOS PRODUTOS POR CATEGORIA COM PAGINAÇÃO E SELEÇÃO */}
            {categorias.map(cat => {
                const produtosDaCategoria = produtos.filter(p => Number(p.categoria_id) === Number(cat.id));
                
                if (produtosDaCategoria.length === 0) return null;

                const paginaAtual = paginaPorCategoria[cat.id] || 1;
                const totalPaginas = Math.ceil(produtosDaCategoria.length / itensPorPagina);
                const inicio = (paginaAtual - 1) * itensPorPagina;
                const produtosPaginados = produtosDaCategoria.slice(inicio, inicio + itensPorPagina);

                const todosDestaCatSelecionados = produtosDaCategoria.every(p => produtosSelecionados.includes(p.id));

                return (
                    <div key={cat.id} style={{ marginBottom: '30px', background: '#fff', padding: '20px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', borderBottom: '2px solid #f1f5f9', paddingBottom: '10px', flexWrap: 'wrap', gap: '10px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                {/* Botão Selecionar Todos da Categoria */}
                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', fontWeight: '600', color: '#334155' }}>
                                    <input 
                                        type="checkbox" 
                                        checked={todosDestaCatSelecionados && produtosDaCategoria.length > 0}
                                        onChange={() => toggleSelecionarTodosDaCategoria(produtosDaCategoria)}
                                        style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                    />
                                    Selecionar Todos
                                </label>

                                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <Folder size={18} color="#ea1d2c" /> {cat.nome} 
                                    <span style={{ fontSize: '12px', background: '#f1f5f9', color: '#64748b', padding: '2px 8px', borderRadius: '12px', fontWeight: 'normal' }}>
                                        {produtosDaCategoria.length} {produtosDaCategoria.length === 1 ? 'item' : 'itens'}
                                    </span>
                                </h3>
                            </div>

                            {/* Controles de Paginação da Categoria */}
                            {totalPaginas > 1 && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', color: '#64748b' }}>
                                    <button 
                                        onClick={() => mudarPaginaCategoria(cat.id, paginaAtual - 1)}
                                        disabled={paginaAtual === 1}
                                        style={{ background: 'none', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '4px 8px', cursor: paginaAtual === 1 ? 'not-allowed' : 'pointer', opacity: paginaAtual === 1 ? 0.5 : 1 }}
                                    >
                                        <ChevronLeft size={16} />
                                    </button>
                                    <span>Pág. <b>{paginaAtual}</b> de {totalPaginas}</span>
                                    <button 
                                        onClick={() => mudarPaginaCategoria(cat.id, paginaAtual + 1)}
                                        disabled={paginaAtual === totalPaginas}
                                        style={{ background: 'none', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '4px 8px', cursor: paginaAtual === totalPaginas ? 'not-allowed' : 'pointer', opacity: paginaAtual === totalPaginas ? 0.5 : 1 }}
                                    >
                                        <ChevronRight size={16} />
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Grid de Cards dos Produtos da Categoria */}
                        <div className="dashboard-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '15px' }}>
                            {produtosPaginados.map((prod) => {
                                const isSelecionado = produtosSelecionados.includes(prod.id);
                                return (
                                    <div key={prod.id} className="card product-admin-card" style={{ background: isSelecionado ? '#fef2f2' : '#f8fafc', padding: '15px', borderRadius: '12px', border: isSelecionado ? '1px solid #f87171' : '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', transition: '0.2s' }}>
                                        <div style={{ display: 'flex', gap: '12px', position: 'relative' }}>
                                            {/* Checkbox de seleção individual */}
                                            <div style={{ position: 'absolute', top: '-4px', left: '-4px', zIndex: 2 }}>
                                                <input 
                                                    type="checkbox" 
                                                    checked={isSelecionado}
                                                    onChange={() => toggleSelecionarProduto(prod.id)}
                                                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                                />
                                            </div>

                                            <div className="prod-img-admin" style={{ width: '60px', height: '60px', borderRadius: '8px', overflow: 'hidden', background: '#e2e8f0', flexShrink: 0, marginLeft: '12px' }}>
                                                {prod.imagem_url ? <img src={prod.imagem_url} alt={prod.nome} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div className="no-image-placeholder" style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ImageIcon size={20} color="#94a3b8" /></div>}
                                            </div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '700', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{prod.nome}</h4>
                                                <div style={{ fontSize: '13px' }}><strong style={{ color: '#ea1d2c' }}>R$ {parseFloat(prod.preco).toFixed(2).replace('.', ',')}</strong></div>
                                            </div>
                                        </div>
                                        <div className="card-footer-admin" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', borderTop: '1px solid #e2e8f0', paddingTop: '10px' }}>
                                            <button onClick={() => {
                                                setEditandoId(prod.id);
                                                setNovoProduto({
                                                    ...prod, 
                                                    preco: String(prod.preco).replace('.', ','), 
                                                    adicionais_ids: prod.adicionais_ids || [],
                                                    exibir_qrcode: prod.exibir_qrcode !== undefined ? prod.exibir_qrcode : 1 
                                                });
                                                setImagePreview(prod.imagem_url);
                                                setShowModal(true);
                                            }} className="btn-edit-outline" style={{ fontSize: '12px', padding: '4px 10px' }}>Configurar</button>
                                            <button onClick={() => { setItemParaExcluir(prod); setShowDeleteModal(true); }} className="btn-delete-icon-only" style={{ background: 'none', border: 'none', cursor: 'pointer' }}><Trash2 size={16} color="#ef4444" /></button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}

            {/* BARRA FLUTUANTE DE AÇÕES EM MASSA */}
            {produtosSelecionados.length > 0 && (
                <div style={{
                    position: 'fixed',
                    bottom: '25px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: '#0f172a',
                    color: '#fff',
                    padding: '12px 24px',
                    borderRadius: '12px',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '20px',
                    zIndex: 4000,
                    animation: 'fadeInUp 0.2s ease-out'
                }}>
                    <span style={{ fontSize: '14px' }}>
                        <b>{produtosSelecionados.length}</b> {produtosSelecionados.length === 1 ? 'produto selecionado' : 'produtos selecionados'}
                    </span>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button 
                            onClick={handleExcluirSelecionados}
                            style={{ background: '#ef4444', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}
                        >
                            <Trash2 size={16} /> Excluir Selecionados
                        </button>
                        <button 
                            onClick={() => setProdutosSelecionados([])}
                            style={{ background: 'transparent', color: '#94a3b8', border: '1px solid #475569', padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}
                        >
                            Limpar Seleção
                        </button>
                    </div>
                </div>
            )}

            {/* MODAL DE IMPORTAÇÃO AUTOMÁTICA & WEBHOOK */}
            {showImportModal && (
                <div className="modal-overlay" style={{zIndex: 2500}}>
                    <div className="modal" style={{ maxWidth: '750px', width: '95%', maxHeight: '90vh', overflowY: 'auto' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h2>📥 Importação Automática de Produtos</h2>
                            <button onClick={() => setShowImportModal(false)} className="btn-close-modal-x"><X size={20} /></button>
                        </div>

                        <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '20px' }}>
                            Escolha o formato de origem ou configure sua integração via Webhook em tempo real.
                        </p>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '20px' }}>
                            {[
                                { id: 'csv', label: 'CSV', icon: <FileText size={20} /> },
                                { id: 'xlsx', label: 'XLSX (Excel)', icon: <Table size={20} /> },
                                { id: 'pdf', label: 'PDF Cardápio', icon: <FileText size={20} /> },
                                { id: 'webhook', label: 'Webhook API', icon: <Webhook size={20} /> }
                            ].map(item => (
                                <div 
                                    key={item.id}
                                    onClick={() => setImportTipo(item.id)}
                                    style={{
                                        padding: '12px',
                                        borderRadius: '12px',
                                        border: importTipo === item.id ? '2px solid #ea1d2c' : '1px solid #e2e8f0',
                                        background: importTipo === item.id ? '#fef2f2' : '#fff',
                                        cursor: 'pointer',
                                        textAlign: 'center',
                                        fontWeight: '600',
                                        fontSize: '13px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}
                                >
                                    {item.icon}
                                    {item.label}
                                </div>
                            ))}
                        </div>

                        {importTipo === 'webhook' ? (
                            <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                    <h4 style={{ margin: 0, fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <Webhook size={18} color="#0284c7" /> Controle & Integração via Webhook
                                    </h4>
                                    
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <span style={{ fontSize: '13px', fontWeight: '600', color: webhookAtivo ? '#16a34a' : '#64748b' }}>
                                            {webhookAtivo ? '🟢 Ativado' : '⚪ Desativado'}
                                        </span>
                                        <label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer' }}>
                                            <input 
                                                type="checkbox" 
                                                checked={webhookAtivo} 
                                                onChange={handleToggleWebhook} 
                                                style={{ opacity: 0, width: 0, height: 0 }}
                                            />
                                            <span style={{
                                                position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                                                backgroundColor: webhookAtivo ? '#ea1d2c' : '#cbd5e1',
                                                transition: '.3s', borderRadius: '24px'
                                            }}>
                                                <span style={{
                                                    position: 'absolute', content: '""', height: '18px', width: '18px', left: webhookAtivo ? '22px' : '3px', bottom: '3px',
                                                    backgroundColor: 'white', transition: '.3s', borderRadius: '50%'
                                                }}></span>
                                            </span>
                                        </label>
                                    </div>
                                </div>

                                <p style={{ fontSize: '12px', color: '#475569', marginBottom: '15px' }}>
                                    {webhookAtivo 
                                        ? 'O endpoint está ativo e recebendo atualizações JSON de produtos automaticamente.' 
                                        : 'O webhook está temporariamente desativado. Requisições POST serão rejeitadas até que seja ativado.'}
                                </p>

                                <label style={{ fontSize: '12px', fontWeight: '700', color: '#334155', display: 'block', marginBottom: '5px' }}>URL do Endpoint:</label>
                                <div style={{ display: 'flex', gap: '8px', marginBottom: '15px' }}>
                                    <input 
                                        type="text" 
                                        readOnly 
                                        value={webhookUrl} 
                                        style={{ flex: 1, padding: '8px 12px', fontSize: '12px', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '6px', color: '#0f172a' }} 
                                    />
                                    <button 
                                        onClick={() => { navigator.clipboard.writeText(webhookUrl); toast.success("URL copiada!"); }}
                                        className="btn-secondary"
                                        style={{ padding: '8px 12px', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: '600' }}
                                    >
                                        <Copy size={14} /> Copiar URL
                                    </button>
                                </div>

                                <div style={{ marginTop: '15px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                                        <label style={{ fontSize: '12px', fontWeight: '700', color: '#334155' }}>Exemplo de Integração (cURL Automático):</label>
                                        <button 
                                            onClick={copiarCurl}
                                            style={{ background: 'none', border: 'none', color: '#ea1d2c', cursor: 'pointer', fontSize: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}
                                        >
                                            {copiedCurl ? <Check size={14} color="#16a34a" /> : <Copy size={14} />} 
                                            {copiedCurl ? 'Copiado!' : 'Copiar cURL'}
                                        </button>
                                    </div>
                                    <pre style={{ 
                                        background: '#0f172a', color: '#38bdf8', padding: '12px', borderRadius: '8px', 
                                        fontSize: '11px', overflowX: 'auto', margin: 0, fontFamily: 'monospace', lineHeight: '1.4' 
                                    }}>
                                        {curlExemplo}
                                    </pre>
                                </div>
                            </div>
                        ) : (
                            <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
                                <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <HelpCircle size={16} color="#0284c7" /> Passo a passo e campos obrigatórios ({importTipo.toUpperCase()})
                                </h4>
                                <p style={{ fontSize: '12px', color: '#475569', margin: '0 0 10px 0' }}>
                                    Seu arquivo <b>.{importTipo}</b> deve conter as seguintes colunas na primeira linha (cabeçalho):
                                </p>
                                <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '12px', color: '#334155', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <li><b>nome</b>: Nome descritivo do produto (Obrigatório)</li>
                                    <li><b>descricao</b>: Ingredientes ou detalhes do prato</li>
                                    <li><b>preco</b>: Valor numérico (Ex: 29.90 ou 29,90) (Obrigatório)</li>
                                    <li><b>estoque</b>: Quantidade disponível (Opcional, padrão 0)</li>
                                    <li><b>categoria</b>: Nome exato ou ID da categoria correspondente</li>
                                </ul>
                            </div>
                        )}

                        {importTipo !== 'webhook' && (
                            <div style={{ textAlign: 'center', padding: '30px', border: '2px dashed #cbd5e1', borderRadius: '16px', background: '#fff' }}>
                                <UploadCloud size={40} color="#94a3b8" style={{ marginBottom: '10px' }} />
                                <p style={{ fontSize: '13px', fontWeight: '600', color: '#334155', margin: '0 0 10px 0' }}>
                                    {importandoLoading ? 'Processando e importando produtos...' : `Clique para selecionar o arquivo .${importTipo.toUpperCase()}`}
                                </p>
                                <input 
                                    type="file" 
                                    ref={importFileInputRef} 
                                    accept={importTipo === 'csv' ? '.csv' : importTipo === 'xlsx' ? '.xlsx,.xls' : '.pdf'} 
                                    onChange={handleImportarArquivo} 
                                    disabled={importandoLoading}
                                    style={{ display: 'none' }}
                                />
                                <button 
                                    type="button" 
                                    onClick={() => importFileInputRef.current.click()} 
                                    disabled={importandoLoading}
                                    className="btn-confirm"
                                    style={{ background: '#ea1d2c', padding: '10px 20px', border: 'none', borderRadius: '8px', color: '#fff', cursor: 'pointer' }}
                                >
                                    {importandoLoading ? 'Carregando...' : 'Selecionar Arquivo'}
                                </button>
                            </div>
                        )}

                        <div className="modal-footer-btns" style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                            <button type="button" onClick={() => setShowImportModal(false)} className="btn-cancel">Fechar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL PRINCIPAL DE EDIÇÃO/CRIAÇÃO */}
            {showModal && (
                <div className="modal-overlay" style={{zIndex: 2000}}>
                    <div className="modal" style={{ maxWidth: '950px', width: '95%', maxHeight: '95vh', overflowY: 'auto' }}>
                        <div className="modal-header-flex">
                            <h2>{editandoId ? '⚙️ Editar Produto' : '✨ Novo Produto'}</h2>
                            <button onClick={fecharModal} className="btn-close-modal-x"><X size={24} /></button>
                        </div>
                         
                        <form onSubmit={handleSalvarProduto}>
                            <div className="modal-grid-config" style={{display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: '20px'}}>
                                <div className="modal-col">
                                    <label className="label">Nome do Produto</label>
                                    <input className="input" value={novoProduto.nome} onChange={e => setNovoProduto({ ...novoProduto, nome: e.target.value })} required />
                                    
                                    <div className="input-group" style={{ marginTop: '15px' }}>
                                        <label className="label">Descrição do Produto</label>
                                        <textarea 
                                            className="input" 
                                            rows="3"
                                            value={novoProduto.descricao || ''} 
                                            onChange={e => setNovoProduto({ ...novoProduto, descricao: e.target.value })} 
                                            placeholder="Descreva os ingredientes, sabor ou detalhes do produto..."
                                            style={{ resize: 'vertical', minHeight: '80px', fontFamily: 'inherit' }}
                                        />
                                    </div>
                                     
                                    <div className="input-row" style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginTop: '15px'}}>
                                        <div className="input-group">
                                            <label className="label">Preço Base (R$)</label>
                                            <input className="input" value={novoProduto.preco} onChange={e => setNovoProduto({ ...novoProduto, preco: e.target.value })} placeholder="0,00" required />
                                        </div>
                                        <div className="input-group">
                                            <label className="label">Categoria</label>
                                            <select className="input" value={novoProduto.categoria_id} onChange={e => setNovoProduto({...novoProduto, categoria_id: e.target.value})}>
                                                <option value="">Selecione...</option>
                                                {categorias.map(cat => <option key={cat.id} value={cat.id}>{cat.nome}</option>)}
                                            </select>
                                        </div>
                                    </div>

                                    {!isBebida() && (
                                        <div className="adicionais-selector-area" style={{marginTop: '25px'}}>
                                            <div style={{display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '15px'}}>
                                                <ListChecks size={18} color="#ea1d2c" />
                                                <h4 style={{margin: 0, fontSize: '14px'}}>Vincular Complementos</h4>
                                            </div>

                                            <div className="accordion-groups">
                                                {['obrigatorio', 'adicional', 'informativo'].map((tipo) => (
                                                    <details key={tipo} className="admin-details" style={{marginBottom: '10px'}} open>
                                                        <summary className="admin-summary" style={{padding: '8px', background: '#f8f9fa', borderRadius: '6px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between'}}>
                                                            <span style={{fontWeight: '600', fontSize: '12px'}}>{tipo.toUpperCase()}</span>
                                                            <ChevronDown size={16} />
                                                        </summary>
                                                        <div className="admin-details-content" style={{padding: '10px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px'}}>
                                                            {gruposDisponiveis.filter(g => g.tipo === tipo).map(grupo => (
                                                                <div key={grupo.id} className={`group-select-card ${novoProduto.adicionais_ids.includes(grupo.id) ? 'selected' : ''}`} 
                                                                    onClick={() => toggleGrupo(grupo.id)}
                                                                    style={{padding: '8px', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', background: novoProduto.adicionais_ids.includes(grupo.id) ? '#fff1f2' : '#fff'}}
                                                                >
                                                                    <input type="checkbox" checked={novoProduto.adicionais_ids.includes(grupo.id)} readOnly />
                                                                    <span style={{fontSize: '12px', marginLeft: '8px'}}>{grupo.nome}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </details>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="modal-col centered">
                                    <label className="label">Foto do Produto</label>
                                    <div className="image-upload-box" onClick={() => fileInputRef.current.click()} style={{height: '250px', border: '2px dashed #ddd', borderRadius: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', overflow: 'hidden'}}>
                                        {imagePreview ? <img src={imagePreview} alt="Preview" style={{width: '100%', height: '100%', objectFit: 'cover'}} /> : <div className="upload-placeholder" style={{textAlign: 'center', color: '#999'}}><ImageIcon size={40} /><p>Clique para enviar</p></div>}
                                    </div>
                                    <input type="file" ref={fileInputRef} hidden onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) { setUploadFile(file); setImagePreview(URL.createObjectURL(file)); }
                                    }} />

                                    <div className="status-config-box" style={{marginTop: '20px', background: '#f8f9fa', padding: '15px', borderRadius: '10px'}}>
                                        <label className="checkbox-label" style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', cursor: 'pointer'}}>
                                            <input type="checkbox" checked={novoProduto.exibir_qrcode === 1} onChange={e => setNovoProduto({ ...novoProduto, exibir_qrcode: e.target.checked ? 1 : 0 })} />
                                            <span style={{fontWeight: '600'}}>Exibir Botão de QR Code no App</span>
                                        </label>

                                        <label className="checkbox-label" style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', cursor: 'pointer'}}>
                                            <input type="checkbox" checked={novoProduto.destaque === 1} onChange={e => setNovoProduto({ ...novoProduto, destaque: e.target.checked ? 1 : 0 })} />
                                            <span style={{fontWeight: '600'}}>Exibir em Destaque ★</span>
                                        </label>
                                        <label className="checkbox-label" style={{display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer'}}>
                                            <input type="checkbox" checked={novoProduto.ativo === 1} onChange={e => setNovoProduto({ ...novoProduto, ativo: e.target.checked ? 1 : 0 })} />
                                            <span style={{fontWeight: '600'}}>Produto Disponível</span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer-btns" style={{marginTop: '30px', display: 'flex', justifyContent: 'flex-end', gap: '10px', borderTop: '1px solid #eee', paddingTop: '20px'}}>
                                <button type="button" onClick={fecharModal} className="btn-cancel">Cancelar</button>
                                <button type="submit" className="btn-confirm" style={{ backgroundColor: '#28a745', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>Salvar Alterações</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showDeleteModal && (
                <div className="modal-overlay" style={{zIndex: 3000}}>
                    <div className="modal-confirm">
                        <AlertTriangle size={48} color="#ea1d2c" style={{marginBottom: '15px'}} />
                        <h3>Excluir Produto?</h3>
                        <p>Tem certeza que deseja remover <b>{itemParaExcluir?.nome}</b>?</p>
                        <div className="modal-buttons">
                            <button onClick={() => setShowDeleteModal(false)} className="btn-cancel">Voltar</button>
                            <button onClick={handleExcluirProduto} className="btn-confirm-delete">Confirmar Exclusão</button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL DE AVISO DE LIMITE DO PLANO */}
            {showPlanLimitModal && (
                <div className="modal-overlay" style={{zIndex: 3500}}>
                    <div className="modal-confirm" style={{ textAlign: 'center', padding: '25px', maxWidth: '420px', width: '90%' }}>
                        <AlertTriangle size={48} color="#f59e0b" style={{marginBottom: '15px'}} />
                        <h3 style={{ marginBottom: '10px', color: '#1e293b' }}>Limite do Plano Atingido</h3>
                        <p style={{ color: '#64748b', fontSize: '14px', lineHeight: '1.5', marginBottom: '20px' }}>
                            O limite de cadastros do seu plano atual foi atingido. Caso queira mais, adquira os planos!
                        </p>
                        <div className="modal-buttons" style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
                            <button 
                                onClick={() => setShowPlanLimitModal(false)} 
                                className="btn-confirm"
                                style={{ background: '#ea1d2c', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}
                            >
                                Entendi
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Produtos;